import defaultState from './defaultState';
import * as C from '../constants';
import TwentyService from '../../../services/twenty-service';
import router from '../../../router';

export default {

  /**
   * Resets the module to it's initial state.
   */
  resetState({ commit }) {
    commit(C.FILTER_STATE_RESET, defaultState());
  },

  /**
   * Retrieves a list of filters.
   */
  fetchFilters({ commit }) {
    commit(C.FILTER_LIST_ITEMS_PENDING);
    return TwentyService.getFilters()
      .then((res) => {
        commit(C.FILTER_LIST_ITEMS_SUCCESS, res.data);
        return Promise.resolve(res.data);
      })
      .catch((err) => {
        commit(C.FILTER_LIST_ITEMS_FAILURE, err.response.data);
        return Promise.reject(err);
      });
  },

  /**
   * Retrieves a single filter.
   *
   * @param commit
   * @param id
   */
  fetchFilter({ commit }, id) {
    commit(C.FILTER_SINGLE_GET_PENDING);
    return TwentyService.getFilter(id)
      .then((res) => {
        commit(C.FILTER_SINGLE_GET_SUCCESS, res.data);
        return Promise.resolve(res.data);
      })
      .catch((err) => {
        commit(C.FILTER_SINGLE_GET_FAILURE);
        return Promise.reject(err);
      });
  },

  /**
   * Retrieves the facilities section of a single filter.
   *
   * @param commit
   * @param id
   */
  fetchFilterFacilities({ commit }, id) {
    commit(C.FILTER_SINGLE_FACILITIES_GET_PENDING);
    return TwentyService.getFilterFacilities(id)
      .then((res) => {
        commit(C.FILTER_SINGLE_FACILITIES_GET_SUCCESS, res.data);
        return Promise.resolve(res.data);
      })
      .catch((err) => {
        commit(C.FILTER_SINGLE_FACILITIES_GET_FAILURE);
        return Promise.reject(err);
      });
  },

  /**
   * Attempts to create a filter
   *
   * @param commit
   * @param name
   * @param product
   * @param brands
   */
  createFilter({ commit }, { name, product, brands }) {
    commit(C.FILTER_LIST_CREATE_FILTER_PENDING, true);
    return TwentyService.createFilter(name, product, brands)
      .then((res) => {
        commit(C.FILTER_LIST_CREATE_FILTER_SUCCESS);
        router.push({ name: 'FilterEdit', params: { id: res.data } });
        return Promise.resolve(res.data);
      })
      .catch(err => Promise.reject(err));
  },

  /**
   * Sets the changed status for a filter's general settings.
   *
   * @param commit
   * @param payload
   */
  setGeneralChanged({ commit }, payload) {
    commit(C.FILTER_SINGLE_GENERAL_CHANGED_SET, payload);
  },

  /**
   * Sets the changed status for a filter's facilities settings.
   *
   * @param commit
   * @param payload
   */
  setFacilitiesChanged({ commit }, payload) {
    commit(C.FILTER_SINGLE_FACILITIES_CHANGED_SET, payload);
  },

  /**
   * Sets the current list of selected brands for the filter list.
   *
   * @param commit
   * @param payload
   */
  setBrands({ commit }, payload) {
    commit(C.FILTER_LIST_BRANDS_SET, payload);
  },

  /**
   * Sets the currently selected product for the filter list.
   *
   * @param dispatch
   * @param commit
   * @param payload
   */
  setProducts({ commit }, payload) {
    commit(C.FILTER_LIST_PRODUCTS_SET, payload);
  },

  /**
   * Sets the rules of the current filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setFilterRules({ commit, dispatch, state }, payload) {
    if (!state.single.general.changed) {
      dispatch('setGeneralChanged', true);
    }
    commit(C.FILTER_SINGLE_GENERAL_RULES_SET, payload);
  },

  /**
   * Sets the default parameters of the current filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setFilterParameters({ commit, dispatch, state }, payload) {
    if (!state.single.general.changed) {
      dispatch('setGeneralChanged', true);
    }
    commit(C.FILTER_SINGLE_GENERAL_PARAMETERS_SET, payload);
  },

  /**
   * Sets the selected brands for the current filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setFilterBrands({ commit, dispatch, state }, payload) {
    if (!state.single.general.changed) {
      dispatch('setGeneralChanged', true);
    }
    commit(C.FILTER_SINGLE_GENERAL_BRANDS_SET, payload);
  },

  /**
   * Sets the selected brands for the current filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setFilterSuppliers({ commit, dispatch, state }, payload) {
    if (!state.single.general.changed) {
      dispatch('setGeneralChanged', true);
    }
    commit(C.FILTER_SINGLE_GENERAL_SUPPLIERS_SET, payload);
  },

  /**
   * Sets the name for the current filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setFilterName({ commit, dispatch, state }, payload) {
    if (!state.single.general.changed) {
      dispatch('setGeneralChanged', true);
    }
    commit(C.FILTER_SINGLE_GENERAL_NAME_SET, payload);
  },

  /**
   * Saves the current filter
   *
   * @param commit
   * @param getters
   * @param dispatch
   * @param payload
   */
  saveFilter({ commit, getters, dispatch }, payload) {
    commit(C.FILTER_SINGLE_UPDATE_PENDING);
    return TwentyService.updateFilter(payload, getters.filterPayload)
      .then(() => {
        dispatch('setGeneralChanged', false);
        commit(C.FILTER_SINGLE_UPDATE_SUCCESS);
        return Promise.resolve();
      })
      .catch(() => {
        commit(C.FILTER_SINGLE_UPDATE_FAILURE);
        return Promise.reject();
      });
  },

  /**
   * Saves the current filter facilities
   *
   * @param commit
   * @param getters
   * @param dispatch
   * @param payload
   */
  saveFilterFacilities({ commit, getters, dispatch }, payload) {
    commit(C.FILTER_SINGLE_FACILITIES_UPDATE_PENDING);
    return TwentyService.updateFilterFacilities(payload, getters.filterFacilitiesPayload)
      .then(() => {
        dispatch('setFacilitiesChanged', false);
        commit(C.FILTER_SINGLE_FACILITIES_UPDATE_SUCCESS);
        return Promise.resolve();
      })
      .catch(() => {
        commit(C.FILTER_SINGLE_FACILITIES_UPDATE_FAILURE);
        return Promise.reject();
      });
  },

  /**
   * Sets the accommodation properties for the filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setAccommodationProperties({ commit, dispatch, state }, payload) {
    if (!state.single.facilities.changed) {
      dispatch('setFacilitiesChanged', true);
    }
    commit(C.FILTER_SINGLE_FACILITIES_PROPERTIES_SET, payload);
  },

  /**
   * Sets the accommodation categories for the filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setAccommodationCategories({ commit, dispatch, state }, payload) {
    if (!state.single.facilities.changed) {
      dispatch('setFacilitiesChanged', true);
    }
    commit(C.FILTER_SINGLE_FACILITIES_CATEGORIES_SET, payload);
  },

  /**
   * Sets the accommodation bedrooms for the filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setAccommodationBedrooms({ commit, dispatch, state }, payload) {
    if (!state.single.facilities.changed) {
      dispatch('setFacilitiesChanged', true);
    }
    commit(C.FILTER_SINGLE_FACILITIES_BEDROOMS_SET, payload);
  },

  /**
   * Sets the site facilities for the filter
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   */
  setSiteFacilities({ commit, dispatch, state }, payload) {
    if (!state.single.facilities.changed) {
      dispatch('setFacilitiesChanged', true);
    }
    commit(C.FILTER_SINGLE_FACILITIES_SITE_FACILITIES_SET, payload);
  },

  /**
   * Deletes a filter.
   *
   * @param context
   * @param payload
   * @returns {*}
   */
  deleteFilter(context, payload) {
    return TwentyService.deleteFilter(payload);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/filter/actions.js